import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { counterpartyKeys, tradesKeys } from '@/services/query-keys';
import { custodianKeys } from '@/services/query-keys/custodian-keys';

export type RegistryName = 'custodians' | 'counterparties';

interface RegistryCopy {
  /** A failed read yields zero rows just like an empty register (LR-572). */
  failed: string;
  emptyLead: string;
  href: string;
  linkLabel: string;
  /** Prefixes to invalidate so a row added elsewhere shows up here. */
  refreshKeys: readonly (readonly unknown[])[];
}

const COPY: Record<RegistryName, RegistryCopy> = {
  custodians: {
    failed:
      "Couldn't load custodians — the list is empty because the registry failed, not because none exist.",
    emptyLead: 'No custodians configured.',
    // The register page opens on its first tab, so name the one we mean.
    href: '/counterparties?tab=custodians',
    linkLabel: 'Add one in Registers → Custodians',
    refreshKeys: [custodianKeys.all],
  },
  counterparties: {
    failed:
      "Couldn't load counterparties — the list is empty because the registry failed, not because none exist.",
    emptyLead: 'No counterparties configured.',
    href: '/counterparties?tab=counterparties',
    linkLabel: 'Add one in Registers → Counterparties',
    // Two homes: the register page's own list and the trade form's copy.
    refreshKeys: [counterpartyKeys.all, [...tradesKeys.all, 'counterparties']],
  },
};

interface RegistryNoticeProps {
  registry: RegistryName;
  /** Field id the notice describes — ids are `${idPrefix}-fetch-error` / `-hint`. */
  idPrefix: string;
  /** Registry genuinely empty: not loading, not failed. */
  empty: boolean;
  /** The read failed AND the list is empty because of it. */
  error: boolean;
}

/**
 * The empty / failed states of a registry-backed select, written once instead of
 * at each of the four call sites. Carries a Refresh: the hint sends the trader
 * to the register in a new tab, and that tab's cache is not this one's — without
 * a refresh the select keeps saying "none configured" for the whole `staleTime`
 * after they added the row it asked for.
 *
 * No `role="alert"`. This is a standing condition of the form when it opens, not
 * a transient event, and the same sentence sits next to up to four selects — one
 * alert per select is read out four times, while designating a single announcer
 * puts it inside a section the trader can collapse. `aria-describedby` on each
 * control (see registryNoticeIds) delivers it at the point of use instead. The
 * register TABLES keep their alert: there the message replaces the whole view and
 * there is no field to describe.
 */
export const RegistryNotice: React.FC<RegistryNoticeProps> = ({
  registry,
  idPrefix,
  empty,
  error,
}) => {
  const queryClient = useQueryClient();
  const copy = COPY[registry];

  if (!empty && !error) return null;

  const refresh = () => {
    copy.refreshKeys.forEach((queryKey) => {
      queryClient.invalidateQueries({ queryKey: queryKey as unknown[] });
    });
  };

  const refreshButton = (label: string) => (
    <button
      type="button"
      onClick={refresh}
      className="text-blue-600 hover:underline focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
    >
      {label}
    </button>
  );

  return (
    <>
      {error && (
        <p id={`${idPrefix}-fetch-error`} className="mt-1 text-xs text-red-600">
          {copy.failed} {refreshButton('Retry')}
        </p>
      )}
      {empty && (
        <p id={`${idPrefix}-hint`} className="mt-1 text-xs text-gray-500">
          {copy.emptyLead}{' '}
          <a
            href={copy.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {copy.linkLabel}
          </a>{' '}
          · {refreshButton('Refresh')}
        </p>
      )}
    </>
  );
};
